import * as d3 from 'd3';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { GeodataMarkerEstimate } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { isDefined } from '../../../../utils/variables';
import { Icon } from '../../icon/icon';
import { GeodataImageItem } from '../geodata-images-state';
import { GeodataContext } from '../geodata-state';
import { getMarkerGroup, pinnedGreen } from './marker-svg-utils';

interface MarkerEstimateThumbnailProps {
  image: GeodataImageItem;
  estimate?: Required<GeodataMarkerEstimate>;
  position: [number, number];
  width: number;
  height: number;
  zoomLevel: number; // Percentage (1-10)
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'PINNED' | 'UNPINNED';
  onClick: () => void;
}

/**
 * Calculates the viewBox to show ±zoomLevel% around the marker position, clamped to image edges.
 * Returns viewBox string: "x y width height"
 */
function calculateViewBox(
  position: [number, number],
  imgWidth: number,
  imgHeight: number,
  zoomLevel: number,
): string {
  const [normalizedX, normalizedY] = position;
  const cropPercent = zoomLevel / 100; // Convert percentage to decimal (e.g., 3% -> 0.03)

  // Calculate crop bounds, clamped to [0, 1]
  const startX = Math.max(0, normalizedX - cropPercent);
  const endX = Math.min(1, normalizedX + cropPercent);
  const startY = Math.max(0, normalizedY - cropPercent);
  const endY = Math.min(1, normalizedY + cropPercent);

  // Convert to pixel coordinates
  const viewX = startX * imgWidth;
  const viewY = startY * imgHeight;
  const viewWidth = (endX - startX) * imgWidth;
  const viewHeight = (endY - startY) * imgHeight;

  return `${viewX} ${viewY} ${viewWidth} ${viewHeight}`;
}

export const MarkerEstimateThumbnail = (props: MarkerEstimateThumbnailProps) => {
  const { signer } = useContext(GeodataContext);
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imgDimensions, setImgDimensions] = useState<{ width: number; height: number } | null>(
    null,
  );

  // Load image when signer and image are available
  useEffect(() => {
    if (!isDefined(signer) || !isDefined(props.image.imageIdentifier)) {
      return;
    }

    const imgElement = new Image();

    if (isDefined(props.image?.file)) {
      // Handle the File case
      const reader = new FileReader();
      reader.onload = () => {
        imgElement.src = reader.result as string;
      };
      reader.readAsDataURL(props.image.file);
    } else {
      // Use previews for thumbnails (smaller, faster)
      imgElement.src = signer.getSignedRequest(`previews/${props.image.imageIdentifier}`);
    }

    imgElement.onload = () => {
      setImageUrl(imgElement.src);
      // Use a fixed width like in place-markers-viewer for consistent scaling
      const imgWidth = 2000;
      const imgHeight = imgElement.naturalHeight * (imgWidth / imgElement.naturalWidth);
      setImgDimensions({ width: imgWidth, height: imgHeight });
      setImageLoaded(true);
    };

    imgElement.onerror = () => {
      setImageLoaded(false);
    };
  }, [signer, props.image]);

  // Render SVG when image is loaded
  useEffect(() => {
    if (!imageLoaded || !isDefined(svgRef.current) || !isDefined(imgDimensions) || !imageUrl) {
      return;
    }

    const svgElement = svgRef.current;
    const { width: imgWidth, height: imgHeight } = imgDimensions;
    const viewBox = calculateViewBox(props.position, imgWidth, imgHeight, props.zoomLevel);

    // Calculate viewBox bounds for marker sizing
    const [normalizedX, normalizedY] = props.position;

    // Clear previous content
    svgElement.innerHTML = '';

    // Set SVG attributes - use display dimensions as viewBox so 1 unit = 1 pixel
    // This makes positioning straightforward: pixel coordinates = SVG coordinates
    svgElement.setAttribute('viewBox', `0 0 ${props.width} ${props.height}`);
    svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Create d3 selection for the SVG
    const svg = d3.select(svgElement);

    // Parse viewBox to get the crop region
    const [viewX, viewY, viewWidth, viewHeight] = viewBox.split(' ').map(Number);

    // Create a group for the scaled/zoomed content (image)
    const scaledGroup = svg.append('g').attr('class', 'scaled-content');

    // Calculate transform to show the cropped region (viewBox) of the image
    // Scale the image to fit the display, showing only the viewBox region
    const scaleX = props.width / viewWidth;
    const scaleY = props.height / viewHeight;
    const scale = Math.min(scaleX, scaleY);

    // Calculate the scaled viewBox dimensions
    const scaledViewWidth = viewWidth * scale;
    const scaledViewHeight = viewHeight * scale;

    // Center the cropped region within the display area
    // Translate to position the viewBox region, then center it
    const translateX = -viewX * scale + (props.width - scaledViewWidth) / 2;
    const translateY = -viewY * scale + (props.height - scaledViewHeight) / 2;

    scaledGroup.attr('transform', `translate(${translateX}, ${translateY}) scale(${scale})`);

    // Create image element inside the scaled group
    scaledGroup
      .append('image')
      .attr('href', imageUrl)
      .attr('width', imgWidth)
      .attr('height', imgHeight);

    // Determine marker color based on status
    let markerColor: string;
    if (props.status === 'PINNED' || props.status === 'ACCEPTED') {
      markerColor = pinnedGreen;
    } else if (props.status === 'UNPINNED') {
      markerColor = theme.color.blue;
    } else if (props.status === 'REJECTED') {
      markerColor = theme.color.red;
    } else {
      // Fallback (PENDING) - use brand.dark
      markerColor = theme.color.brand.dark;
    }

    // Create a separate group for the marker that doesn't scale
    // Marker uses fixed pixel sizes and is positioned in screen/pixel coordinates
    const markerContainerGroup = svg.append('g').attr('class', 'marker-container');

    // Calculate marker position in screen/pixel coordinates
    // Transform from image coordinates to screen coordinates using the same transform as the scaled group
    const markerXInImage = normalizedX * imgWidth;
    const markerYInImage = normalizedY * imgHeight;

    // Apply the same transform as the scaled group to get screen position
    const markerScreenX = markerXInImage * scale + translateX;
    const markerScreenY = markerYInImage * scale + translateY;

    // Use fixed pixel sizes for the marker (not affected by any scaling)
    const size = 20; // Fixed pixel size
    const strokeWidth = 2; // Fixed stroke width

    // Create marker using getMarkerGroup
    // We pass [0, 0] for position since we'll handle positioning via transform
    const markerResult = getMarkerGroup(
      svg,
      [0, 0], // Position will be set via transform
      markerColor,
      size,
      strokeWidth,
    );

    // Move the marker group to the markerContainerGroup and apply positioning transform
    const markerNode = markerResult.element.node();
    if (markerNode && markerNode.parentNode) {
      markerNode.parentNode.removeChild(markerNode);
      markerContainerGroup.node()?.appendChild(markerNode);
    }

    // Apply transform to position the marker at the calculated screen coordinates
    markerResult.element.attr('transform', `translate(${markerScreenX}, ${markerScreenY})`);
  }, [
    imageLoaded,
    imgDimensions,
    imageUrl,
    props.position,
    props.zoomLevel,
    props.status,
    theme,
    props.width,
    props.height,
  ]);

  return (
    <>
      <ThumbnailSvg
        height={props.height}
        ref={svgRef}
        style={{ display: imageLoaded ? 'block' : 'none' }}
        width={props.width}
        onClick={props.onClick}
      />
      {!imageLoaded && (
        <LoadingPlaceholder>
          <Icon color="#ffffff" icon={['fal', 'map-marker']} size="2x" />
        </LoadingPlaceholder>
      )}
    </>
  );
};

const ThumbnailSvg = styled.svg`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: #424242;
`;

const LoadingPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #424242;
`;
